/**
 * Reusable confirmation dialog — frame-styled modal with configurable
 * title, description, icon, and confirm/cancel actions.
 *
 * Renders via portal to escape parent clipping. Dismiss by clicking
 * outside or pressing Escape.
 *
 * Variants:
 *  - "danger" (default) — red confirm button (delete, remove)
 *  - "default" — accent confirm button (approve, confirm)
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cva } from "class-variance-authority";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";
import { useDialogKeyboard } from "@/hooks/use-dialog-keyboard";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import "./transitions-recipes.css";
import { useTransitionsRecipeEntry } from "./use-transitions-recipe-entry";
import { Button } from "./button";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "./surface-contract";

type ConfirmVariant = "danger" | "default";
const MODAL_CLOSE_MS = 150;

/**
 * Confirm-dialog panel recipe — Tahoe direction (approved 2026-06-10).
 *
 * Layout only. The menu surface role owns fill/stroke/shadow/radius so
 * confirmations match dropdowns, context menus, submenus, and native glass.
 */
export const confirmDialogPanelVariants = cva("flex w-80 flex-col");

interface ConfirmDialogProps {
  /** Dialog title shown in the header */
  title: string;
  /** Description text in the body */
  description: string;
  /** Label for the confirm button (default: "Confirm") */
  confirmLabel?: string;
  /** Label for the cancel button (default: "Cancel") */
  cancelLabel?: string;
  /** Visual variant — "danger" for destructive actions, "default" for neutral */
  variant?: ConfirmVariant;
  /** Optional icon/avatar element rendered in the header */
  icon?: React.ReactNode;
  /** Pending callers reject dismissal before an exit can start. */
  busy?: boolean;
  /** Ignore Confirm this long after opening, so a click that opened the
   * dialog (a double-click) cannot land on Confirm too. */
  armDelayMs?: number;
  onConfirm: () => void;
  onCancel: () => void;
  "data-testid"?: string;
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = "danger",
  icon,
  busy = false,
  armDelayMs = 0,
  onConfirm,
  onCancel,
  "data-testid": testId = "confirm-dialog",
}: ConfirmDialogProps) {
  const t = useT(uiMessages);
  const tc = useT(commonMessages);
  const dialogRef = useRef<HTMLDivElement>(null);
  const entry = useTransitionsRecipeEntry();
  const entryRef = entry.ref;
  const setDialogRef = useCallback(
    (node: HTMLDivElement | null) => {
      dialogRef.current = node;
      entryRef(node);
    },
    [entryRef],
  );
  const cancelRef = useRef<HTMLButtonElement>(null);
  const callbacks = useRef({ onConfirm, onCancel, busy });
  callbacks.current = { onConfirm, onCancel, busy };
  const cancelRequested = useRef(false);
  const confirming = useRef(false);
  const openedAt = useRef<number | null>(null);
  const [closing, setClosing] = useState(false);
  const reducedMotion = useReducedMotion();

  const requestCancel = useCallback(() => {
    if (
      callbacks.current.busy ||
      cancelRequested.current ||
      confirming.current
    ) {
      return;
    }
    cancelRequested.current = true;
    setClosing(true);
  }, []);
  const confirm = useCallback(() => {
    if (
      callbacks.current.busy ||
      cancelRequested.current ||
      confirming.current ||
      openedAt.current === null ||
      Date.now() - openedAt.current < armDelayMs
    ) {
      return;
    }
    confirming.current = true;
    try {
      // Confirm callers own pending/error state and can unmount immediately.
      // Preserve their callback timing; only dismissal owns a delayed exit.
      callbacks.current.onConfirm();
    } finally {
      confirming.current = false;
    }
  }, [armDelayMs]);

  useEffect(() => {
    openedAt.current = Date.now();
  }, []);

  useDialogKeyboard({
    dialogRef,
    initialFocusRef: cancelRef,
    focusKey: `${testId}:${closing}:${busy}`,
    onClose: requestCancel,
  });

  useLayoutEffect(() => {
    if (closing || busy) dialogRef.current?.focus();
  }, [closing, busy]);

  useEffect(() => {
    if (!closing) return;
    const finish = () => {
      if (!cancelRequested.current) return;
      setClosing(false);
      try {
        callbacks.current.onCancel();
      } finally {
        cancelRequested.current = false;
      }
    };
    if (reducedMotion) {
      finish();
      return;
    }
    const timer = window.setTimeout(finish, MODAL_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [closing, reducedMotion]);

  return createPortal(
    // z-31800: above modal-tier overlays and below onboarding at 32000.
    <div
      className="fixed inset-0 z-[31800] flex items-center justify-center bg-black/30"
      onClick={requestCancel}
      onKeyDown={(event) => {
        // React portal events bubble through their original parent. Claim
        // Escape before a Settings pocket or toast can navigate/unmount us.
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        requestCancel();
      }}
      data-interactive="true"
      data-testid={`${testId}-backdrop`}
    >
      <div
        ref={setDialogRef}
        data-entered={entry.entered ? "true" : "false"}
        tabIndex={-1}
        data-state={closing ? "closed" : "open"}
        aria-busy={closing || busy || undefined}
        className={cn(
          surfaceRoleClassName("menu"),
          "denker-transition-modal",
          confirmDialogPanelVariants(),
        )}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        aria-describedby={`${testId}-description`}
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
      >
        {/* Header */}
        <div className="flex items-center gap-appkit-xs border-b border-glass-stroke-faint px-[20px] py-appkit-sm">
          {icon}
          <span
            id={`${testId}-title`}
            className="text-appkit-body font-semibold text-primary"
          >
            {title}
          </span>
        </div>

        {/* Content */}
        <div className="px-[20px] py-appkit-sm">
          <p
            id={`${testId}-description`}
            className="text-appkit-body text-secondary"
          >
            {description}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-appkit-xs border-t border-glass-stroke-faint px-[20px] py-appkit-sm">
          <Button
            type="button"
            ref={cancelRef}
            onClick={requestCancel}
            disabled={closing || busy}
            variant="secondary"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel ?? tc("cancel")}
          </Button>
          <Button
            type="button"
            onClick={confirm}
            disabled={closing || busy}
            variant={variant === "danger" ? "destructive" : "default"}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel ?? t("confirm")}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
